import { create } from 'zustand';

import type { CompoundType } from '@polyai/common/types/helpers';

import {
  PROJECT_RESOURCE,
  type ProjectResource,
  useProjectResources,
} from 'hooks/useProjectResources';
import useSampleProject from 'hooks/useSampleProjects/useSampleProject';

type Resources = {
  resources: {
    [key in ProjectResource]: boolean;
  };
};

type Preview = {
  preview: boolean;
};

type ReadonlyInternal = CompoundType<
  Resources &
    Preview & {
      setResourceReadOnly: (resource: ProjectResource, value: boolean) => void;
      setPreview: (value: boolean) => void;
    }
>;

type SyncStore = {
  resource: ProjectResource;
  readonlyValue: boolean | undefined;
  previewValue: boolean | undefined;
};

export const syncStore = ({
  resource,
  readonlyValue,
  previewValue,
}: SyncStore) => {
  const { resources, setResourceReadOnly, preview, setPreview } =
    permissionStore.getState();
  if (resources[resource] !== readonlyValue) {
    setResourceReadOnly(resource, !!readonlyValue);
  }
  if (preview !== previewValue) {
    setPreview(!!previewValue);
  }
};

const permissionStore = create<ReadonlyInternal>(set => ({
  resources: Object.values(PROJECT_RESOURCE).reduce(
    (acc, resource) => {
      acc[resource] = false;
      return acc;
    },
    {} as { [key in ProjectResource]: boolean },
  ),
  readonly: false,
  setResourceReadOnly: (resource: ProjectResource, value: boolean) =>
    set(state => {
      return {
        resources: {
          ...state.resources,
          [resource]: value,
        },
      };
    }),
  preview: false,
  setPreview: (value: boolean) => set(() => ({ preview: value })),
}));

type UseViewModeProps = {
  resource?: ProjectResource;
};

export const useViewMode = ({ resource }: UseViewModeProps = {}) => {
  const { inSampleProject } = useSampleProject();
  const { isProjectReadOnly } = useProjectResources();
  const readonly =
    (resource
      ? permissionStore(state => state.resources[resource])
      : isProjectReadOnly) || inSampleProject;

  const preview = permissionStore(state => state.preview) || inSampleProject;

  return {
    readonly,
    preview,
    isEditable: !readonly && !preview && !inSampleProject,
    isSampleProject: inSampleProject,
  };
};
